/*
  Lumra — Homepage Motion Controller
  ------------------------------------------------------------
  One small, dependency-free engine for the whole homepage:
    1. opts into the CSS hidden-initial-state (html.lumra-motion) — but ONLY
       when motion is allowed, so no-JS / reduced-motion always ships visible;
    2. reveals [data-reveal] and legacy .lumra-reveal elements on scroll, with
       per-group stagger;
    3. runs the hero load-in (Ken Burns settle + copy sequence);
    4. drives a subtle hero scroll-parallax via a CSS custom property.

  Paired with assets/lumra-motion.css. Pairs of transform properties are kept
  on separate elements so parallax (wrapper) and Ken Burns (media) never fight.
  Filename kept as lumra-reveal.js so layout/theme.liquid's include still loads.
*/
(function () {
  'use strict';

  var root = document.documentElement;
  var reduce = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  // Opt into the hidden-initial-state CSS only when we can actually animate.
  // Without this class the page renders fully visible (the safe default).
  if (!reduce) root.classList.add('lumra-motion');

  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  ready(function () {
    var hero = document.querySelector('.lumra-hero-v2');

    /* ---- 1. Hero load-in ------------------------------------------------ */
    // Double-rAF so the initial (hidden/scaled) state paints once before the
    // .is-loaded transition kicks in — otherwise it can snap without easing.
    if (hero) {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { hero.classList.add('is-loaded'); });
      });
      // Stagger the hero copy in document order.
      var heroBits = hero.querySelectorAll('[data-hero]');
      for (var h = 0; h < heroBits.length; h++) {
        heroBits[h].style.transitionDelay = (120 + h * 130) + 'ms';
      }
    }

    /* ---- 2. Scroll reveals --------------------------------------------- */
    var items = document.querySelectorAll('[data-reveal], .lumra-reveal');

    function show(el) {
      el.classList.add('is-in');
      el.classList.add('is-visible'); // legacy .lumra-reveal hook
    }

    if (reduce || !('IntersectionObserver' in window) || !items.length) {
      for (var i = 0; i < items.length; i++) { show(items[i]); }
    } else {
      // Stagger children within any [data-reveal-group] before observing.
      var groups = document.querySelectorAll('[data-reveal-group]');
      for (var g = 0; g < groups.length; g++) {
        var kids = groups[g].querySelectorAll('[data-reveal]');
        for (var k = 0; k < kids.length; k++) {
          kids[k].style.transitionDelay = (k * 85) + 'ms';
        }
      }

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          show(entry.target);
          io.unobserve(entry.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

      for (var o = 0; o < items.length; o++) { io.observe(items[o]); }
    }

    /* ---- 3. Scroll parallax ---------------------------------------------
       Generic: any [data-parallax] element drifts slower than the page
       (factor < 1, overridable via data-parallax-factor). Overscan in the
       CSS (inset: -14% or similar) hides the exposed edge. Transform-only,
       rAF-throttled. Covers the hero media and the editorial-break media. */
    if (!reduce) {
      var parallaxEls = document.querySelectorAll('[data-parallax]');
      if (parallaxEls.length) {
        var pTicking = false;
        var applyParallax = function () {
          for (var p = 0; p < parallaxEls.length; p++) {
            var el = parallaxEls[p];
            var host = el.closest('[data-parallax-scope]') || el.parentElement;
            var rect = host.getBoundingClientRect();
            if (rect.bottom > 0 && rect.top < window.innerHeight) {
              var factor = parseFloat(el.getAttribute('data-parallax-factor')) || 0.12;
              var shift = Math.round(rect.top * -factor);
              el.style.setProperty('--lumra-parallax', shift + 'px');
            }
          }
          pTicking = false;
        };
        var onParallaxScroll = function () {
          if (!pTicking) { pTicking = true; requestAnimationFrame(applyParallax); }
        };
        window.addEventListener('scroll', onParallaxScroll, { passive: true });
        window.addEventListener('resize', onParallaxScroll, { passive: true });
        applyParallax();
      }
    }

    /* ---- 4. Mobile carousel dots ---------------------------------------
       Each [data-carousel] gets a sibling [data-carousel-dots] populated with
       one button per direct child. Clicking scrolls to that card; scroll
       position (throttled) updates which dot is active. Snap/scroll itself
       is pure CSS — this only drives the indicator. */
    var carousels = document.querySelectorAll('[data-carousel]');
    for (var c = 0; c < carousels.length; c++) {
      (function (track) {
        var dotsHost = track.parentElement.querySelector('[data-carousel-dots]');
        var cards = track.children;
        if (!dotsHost || cards.length < 2) return;

        var dots = [];
        for (var d = 0; d < cards.length; d++) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'lumra-carousel-dots__dot' + (d === 0 ? ' is-active' : '');
          dot.setAttribute('aria-label', 'Go to item ' + (d + 1));
          (function (index) {
            dot.addEventListener('click', function () {
              cards[index].scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', inline: 'start', block: 'nearest' });
            });
          })(d);
          dotsHost.appendChild(dot);
          dots.push(dot);
        }
        dotsHost.removeAttribute('aria-hidden');

        var cTicking = false;
        var updateActive = function () {
          var trackRect = track.getBoundingClientRect();
          var center = trackRect.left + trackRect.width / 2;
          var closest = 0;
          var closestDist = Infinity;
          for (var i = 0; i < cards.length; i++) {
            var r = cards[i].getBoundingClientRect();
            var dist = Math.abs((r.left + r.width / 2) - center);
            if (dist < closestDist) { closestDist = dist; closest = i; }
          }
          for (var j = 0; j < dots.length; j++) {
            dots[j].classList.toggle('is-active', j === closest);
          }
          cTicking = false;
        };
        track.addEventListener('scroll', function () {
          if (!cTicking) { cTicking = true; requestAnimationFrame(updateActive); }
        }, { passive: true });
      })(carousels[c]);
    }

    /* ---- 5. Image fade-in ------------------------------------------------
       Every [data-fade-img] fades in on load rather than popping in (animation
       guide: "Every image fades. Duration 300ms. Never suddenly appears."). If
       already decoded from cache, show immediately — no flash of blank space. */
    var fadeImgs = document.querySelectorAll('[data-fade-img]');
    for (var f = 0; f < fadeImgs.length; f++) {
      (function (img) {
        if (img.complete && img.naturalWidth > 0) {
          img.classList.add('is-loaded');
        } else {
          img.addEventListener('load', function () { img.classList.add('is-loaded'); }, { once: true });
        }
      })(fadeImgs[f]);
    }
  });
})();
